// ============================================
// AvatarCropModal — pan/zoom/crop a chosen photo before it becomes the avatar
// ============================================
import { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import Modal from '../Modal/Modal';
import styles from './AvatarCropModal.module.css';

// Defaults: the user's own profile photo — a 512px JPEG, zoom from "fill" up.
// Admin → Avatars passes PNG output, a larger size and zoom-out (padding).
const DEFAULT_OUTPUT = Object.freeze({
    format: 'image/jpeg', // 'image/jpeg' | 'image/png'
    size: 512,            // exported square size (px); with `maxSize`, the cropped area's own size up to maxSize
    maxSize: null,
    minSourcePx: null,    // refuse crops smaller than this many source pixels (would be upscaled)
    maxBytes: null,       // PNG over this falls back to WEBP, then JPEG
});
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('This image could not be opened. Try a JPG or PNG.'));
        img.src = src;
    });
}

const EXTENSIONS = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

function toBlob(canvas, type) {
    return new Promise((resolve) => canvas.toBlob(resolve, type, 0.9));
}

export function outputSizeFor(area, output) {
    return output.maxSize ? Math.min(output.maxSize, Math.max(output.size, Math.round(area.width))) : output.size;
}

// Draws the selected area of the image onto a square canvas and returns a File.
// The area may extend past the image when zoomed out; that part stays empty
// (transparent for PNG, white for JPEG).
async function cropToFile(imageSrc, area, output) {
    const img = await loadImage(imageSrc);
    const size = outputSizeFor(area, output);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const opaque = output.format === 'image/jpeg';
    // JPEG has no transparency — fill so transparent PNGs don't turn black.
    if (opaque) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
    }
    ctx.imageSmoothingQuality = 'high';
    const scale = size / area.width;
    ctx.drawImage(img, -area.x * scale, -area.y * scale, img.naturalWidth * scale, img.naturalHeight * scale);

    let blob = await toBlob(canvas, output.format);
    // Keep under the upload limit: PNG → WEBP (keeps transparency) → JPEG.
    if (output.maxBytes && blob && blob.size > output.maxBytes && !opaque) {
        const webp = await toBlob(canvas, 'image/webp');
        // Browsers that can't encode WEBP silently return PNG.
        if (webp && webp.type === 'image/webp' && webp.size <= output.maxBytes) {
            blob = webp;
        } else {
            const flat = document.createElement('canvas');
            flat.width = size;
            flat.height = size;
            const fctx = flat.getContext('2d');
            fctx.fillStyle = '#ffffff';
            fctx.fillRect(0, 0, size, size);
            fctx.drawImage(canvas, 0, 0);
            blob = await toBlob(flat, 'image/jpeg');
        }
    }
    if (!blob) throw new Error('Could not process this image.');
    const type = blob.type || output.format;
    return new File([blob], `avatar.${EXTENSIONS[type] || 'png'}`, { type });
}

function AvatarCropModal({
    imageSrc,
    onCancel,
    onSave,
    title = 'Move and scale',
    saveLabel = 'Save photo',
    output: outputOverrides = null,
    minZoom = MIN_ZOOM,
    maxZoom = MAX_ZOOM,
}) {
    const output = { ...DEFAULT_OUTPUT, ...outputOverrides };
    // Zooming out past "fill" leaves empty space around the image (padding).
    const allowZoomOut = minZoom < 1;
    const [error, setError] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [areaPixels, setAreaPixels] = useState(null);
    const [saving, setSaving] = useState(false);

    const handleCropComplete = useCallback((_area, pixels) => setAreaPixels(pixels), []);

    const handleClose = useCallback(() => {
        if (!saving) onCancel();
    }, [saving, onCancel]);

    const handleSave = async () => {
        if (!areaPixels || saving) return;
        if (output.minSourcePx && areaPixels.width < output.minSourcePx) {
            setError(`The selected area is only ${Math.round(areaPixels.width)} px wide — zoom out so it covers at least ${output.minSourcePx} px of the image.`);
            return;
        }
        setError(null);
        setSaving(true);
        try {
            const file = await cropToFile(imageSrc, areaPixels, output);
            await onSave(file);
        } catch (err) {
            alert(err.message || 'Could not process this image.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen onClose={handleClose}>
            <div className={styles.content}>
                <h3 className={styles.title}>{title}</h3>

                <div className={styles.cropArea}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        minZoom={minZoom}
                        maxZoom={maxZoom}
                        restrictPosition={!allowZoomOut}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={handleCropComplete}
                        mediaProps={{
                            // e.g. HEIC in browsers that can't decode it
                            onError: () => {
                                alert('This image could not be opened. Try a JPG or PNG.');
                                onCancel();
                            },
                        }}
                    />
                </div>

                <label className={styles.zoomRow}>
                    <span className={styles.zoomIcon} aria-hidden="true">−</span>
                    <input
                        type="range"
                        min={minZoom}
                        max={maxZoom}
                        step={0.01}
                        value={zoom}
                        onChange={(e) => setZoom(Number(e.target.value))}
                        className={styles.zoomSlider}
                        aria-label="Zoom"
                    />
                    <span className={styles.zoomIcon} aria-hidden="true">+</span>
                </label>

                {error && <p className={styles.error} role="alert">{error}</p>}

                <div className={styles.actions}>
                    <button type="button" className={styles.cancelBtn} onClick={handleClose} disabled={saving}>
                        Cancel
                    </button>
                    <button type="button" className={styles.saveBtn} onClick={handleSave} disabled={saving || !areaPixels}>
                        {saving ? 'Saving…' : saveLabel}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default AvatarCropModal;
